import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { RefreshCw, Wrench, AlertTriangle, CheckCircle2, UserX, UserCheck, Stethoscope, Loader2 } from 'lucide-react'
import { SuperAdminHeader } from '@/features/super-admin/components/SuperAdminHeader'
import { useMenuToggle } from '@/features/super-admin/super-admin-shell-context'
import {
  useMaintenanceIssues,
  useFixOrphanedAuthUser,
  useFixProfileWithoutAuth,
  useFixMemberWithoutProfessional,
  useFixAllMembersWithoutProfessional,
  type OrphanedAuthUser,
  type ProfileWithoutAuth,
  type MemberWithoutProfessional,
} from '@/features/super-admin/hooks/use-maintenance'

export const Route = createFileRoute('/super-admin/maintenance/')({
  component: MaintenancePage,
})

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Erro ao corrigir'
}

function MaintenancePage() {
  const { onMenuToggle } = useMenuToggle()
  const { data, isLoading, refetch, isFetching } = useMaintenanceIssues()
  const fixOrphaned = useFixOrphanedAuthUser()
  const fixProfileAuth = useFixProfileWithoutAuth()
  const fixMember = useFixMemberWithoutProfessional()
  const fixAllMembers = useFixAllMembersWithoutProfessional()

  const [fixingId, setFixingId] = useState<string | null>(null)
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message })
    setTimeout(() => setToast(null), 4000)
  }

  const totalIssues =
    (data?.orphaned_auth_users.length || 0) +
    (data?.profiles_without_auth.length || 0) +
    (data?.members_without_professional.length || 0)

  const handleFixOrphaned = async (user: OrphanedAuthUser) => {
    setFixingId(user.auth_user_id)
    try {
      await fixOrphaned.mutateAsync(user.auth_user_id)
      showToast('success', `Perfil criado para ${user.email}`)
    } catch (err: unknown) {
      showToast('error', getErrorMessage(err))
    } finally {
      setFixingId(null)
    }
  }

  const handleFixProfileAuth = async (profile: ProfileWithoutAuth) => {
    setFixingId(profile.profile_id)
    try {
      const result = await fixProfileAuth.mutateAsync(profile.profile_id)
      if (result.matched) {
        showToast('success', `Perfil ${profile.email} vinculado ao auth user`)
      } else {
        showToast('error', `Nenhum auth user encontrado com email ${profile.email}`)
      }
    } catch (err: unknown) {
      showToast('error', getErrorMessage(err))
    } finally {
      setFixingId(null)
    }
  }

  const handleFixMember = async (member: MemberWithoutProfessional) => {
    setFixingId(member.profile_id)
    try {
      await fixMember.mutateAsync(member.profile_id)
      showToast('success', `Registro de profissional criado para ${member.full_name || member.email}`)
    } catch (err: unknown) {
      showToast('error', getErrorMessage(err))
    } finally {
      setFixingId(null)
    }
  }

  const handleFixAllMembers = async () => {
    setFixingId('all-members')
    try {
      const result = await fixAllMembers.mutateAsync()
      showToast('success', `${result.fixed} registros de profissionais criados`)
    } catch (err: unknown) {
      showToast('error', getErrorMessage(err))
    } finally {
      setFixingId(null)
    }
  }

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })

  return (
    <div className="flex flex-col h-full">
      <SuperAdminHeader
        title="Manutenção de Dados"
        subtitle="Detecte e corrija inconsistências no banco de dados"
        onMenuToggle={onMenuToggle}
      />
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${
            toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          }`}
        >
          {toast.type === 'success' ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertTriangle className="w-4 h-4 shrink-0" />}
          {toast.message}
        </div>
      )}

      <div className="flex-1 overflow-auto p-4 md:p-6 max-w-5xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${totalIssues > 0 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
            {isLoading ? '...' : totalIssues === 0 ? 'Sem problemas encontrados' : `${totalIssues} problema${totalIssues !== 1 ? 's' : ''} encontrado${totalIssues !== 1 ? 's' : ''}`}
          </div>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          Verificar novamente
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="space-y-6">

          {/* Section: Membros sem registro de profissional */}
          <IssueSection
            icon={<Stethoscope className="w-5 h-5" />}
            title="Legado de profissionais removido"
            description="A tabela legada health_professionals saiu do modelo ativo. Esta seção permanece vazia apenas para compatibilidade temporária da tela de manutenção."
            count={data?.members_without_professional.length || 0}
            headerAction={
              (data?.members_without_professional.length || 0) > 1 ? (
                <button
                  onClick={handleFixAllMembers}
                  disabled={fixingId === 'all-members'}
                  className="flex items-center gap-2 px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700 transition-colors disabled:opacity-50"
                >
                  {fixingId === 'all-members' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wrench className="w-3 h-3" />}
                  Corrigir todos
                </button>
              ) : null
            }
          >
            {data?.members_without_professional.map((member) => (
              <IssueRow
                key={member.member_id}
                title={member.full_name || member.email}
                subtitle={`${member.email} · ${member.organization_name} · ${member.role}`}
                date={formatDate(member.joined_at)}
                fixing={fixingId === member.profile_id}
                onFix={() => handleFixMember(member)}
                fixLabel="Criar registro"
              />
            ))}
          </IssueSection>

          {/* Section: Perfis sem vínculo de auth */}
          <IssueSection
            icon={<UserCheck className="w-5 h-5" />}
            title="Perfis sem vínculo com usuário de autenticação"
            description="Perfis na tabela public.profiles que não possuem auth_user_id. Podem ser usuários pendentes ou com cadastro incompleto."
            count={data?.profiles_without_auth.length || 0}
          >
            {data?.profiles_without_auth.map((profile) => (
              <IssueRow
                key={profile.profile_id}
                title={profile.full_name || profile.email}
                subtitle={profile.email}
                date={formatDate(profile.created_at)}
                fixing={fixingId === profile.profile_id}
                onFix={() => handleFixProfileAuth(profile)}
                fixLabel="Vincular por email"
              />
            ))}
          </IssueSection>

          {/* Section: Auth users sem perfil */}
          <IssueSection
            icon={<UserX className="w-5 h-5" />}
            title="Usuários de autenticação sem perfil"
            description="Usuários que existem no sistema de autenticação (auth.users) mas não possuem perfil correspondente em public.profiles."
            count={data?.orphaned_auth_users.length || 0}
          >
            {data?.orphaned_auth_users.map((user) => (
              <IssueRow
                key={user.auth_user_id}
                title={user.email}
                subtitle={`ID: ${user.auth_user_id}`}
                date={formatDate(user.created_at)}
                fixing={fixingId === user.auth_user_id}
                onFix={() => handleFixOrphaned(user)}
                fixLabel="Criar perfil"
              />
            ))}
          </IssueSection>

        </div>
      )}
      </div>
    </div>
  )
}

interface IssueSectionProps {
  icon: React.ReactNode
  title: string
  description: string
  count: number
  headerAction?: React.ReactNode
  children?: React.ReactNode
}

function IssueSection({ icon, title, description, count, headerAction, children }: IssueSectionProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className={`mt-0.5 p-2 rounded-lg ${count > 0 ? 'bg-amber-100 text-amber-600' : 'bg-green-100 text-green-600'}`}>
            {icon}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900 text-sm">{title}</h3>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${count > 0 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                {count}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">{description}</p>
          </div>
        </div>
        {headerAction}
      </div>

      {count === 0 ? (
        <div className="px-6 py-4 flex items-center gap-2 text-sm text-green-600">
          <CheckCircle2 className="w-4 h-4" />
          Nenhum problema encontrado
        </div>
      ) : (
        <div className="divide-y divide-gray-50">{children}</div>
      )}
    </div>
  )
}

interface IssueRowProps {
  title: string
  subtitle: string
  date: string
  fixing: boolean
  onFix: () => void
  fixLabel: string
}

function IssueRow({ title, subtitle, date, fixing, onFix, fixLabel }: IssueRowProps) {
  return (
    <div className="px-6 py-3 flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{title}</p>
          <p className="text-xs text-gray-500 truncate">{subtitle}</p>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="text-xs text-gray-400">{date}</span>
        <button
          onClick={onFix}
          disabled={fixing}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-xs font-medium hover:bg-amber-100 transition-colors disabled:opacity-50"
        >
          {fixing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wrench className="w-3 h-3" />}
          {fixLabel}
        </button>
      </div>
    </div>
  )
}
